import { useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import { Badge, Box, Button, Chip, CircularProgress, Divider, Drawer, Stack, Typography } from '@mui/material';

import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import { APPSYNC_EVENTS_WS_DEBUG, getSubFromIdToken, subscribeUserNotificationsWS } from 'utils/appsyncEventsWs';

import { APIAction } from 'utils/action-manager/api/apiManager';
import handleAction from 'utils/action-manager/actionManager';

import { Add, NotificationStatus, PlayCircle } from 'iconsax-react';

import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { ThemeMode } from 'types/config';

type WhatsNewItem = {
  id: number;
  notificationId: number;
  type?: string | null;
  tag?: string | null;
  title?: string | null;
  description?: string | null;
  titleI18n?: Record<string, string> | null;
  descriptionI18n?: Record<string, string> | null;
  titleResolved?: string | null;
  descriptionResolved?: string | null;
  title_resolved?: string | null;
  description_resolved?: string | null;
  imageUrl?: string | null;
  imageurl?: string | null;
  videoUrl?: string | null;
  videourl?: string | null;
  payloadJson?: any;
  payloadjson?: any;
  createdAtIso?: string | null;
  createdat_iso?: string | null;
  createdat?: string | null;
  seenAtIso?: string | null;
  seenat_iso?: string | null;
  seenat?: string | null;
  readAtIso?: string | null;
  readat_iso?: string | null;
  readat?: string | null;
  isRead?: boolean;
  isSeen?: boolean;
};

type NormalizedWhatsNewItem = WhatsNewItem & {
  id: number;
  notificationId: number;
  titleResolved: string | null;
  descriptionResolved: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  payloadJson: any;
  createdAtIso: string | null;
  seenAtIso: string | null;
  readAtIso: string | null;
  isRead: boolean;
  isSeen: boolean;
};

function isValidUrl(value: string | null | undefined) {
  if (!value || !value.trim()) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function resolveAppFromHost(hostname: string) {
  const h = (hostname || '').toLowerCase();
  if (h.includes('admin.')) return 'admin';
  if (h.includes('agent.')) return 'agent';
  if (h.includes('developer.')) return 'developer';
  if (h.includes('collab.')) return 'collab';
  return 'agent';
}

function parseMaybeJson(value: any) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

function getYouTubeId(url: string): string | null {
  if (!isValidUrl(url)) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id || null;
    }

    if (host.includes('youtube.com')) {
      const watchId = parsed.searchParams.get('v');
      if (watchId) return watchId;

      const pathParts = parsed.pathname.split('/').filter(Boolean);
      const embedIndex = pathParts.findIndex((part) => part === 'embed' || part === 'shorts');
      if (embedIndex >= 0 && pathParts[embedIndex + 1]) return pathParts[embedIndex + 1];
    }
  } catch {
    return null;
  }

  return null;
}

function getVideoThumbnail(item: NormalizedWhatsNewItem): string | null {
  const payload = item.payloadJson || {};
  const payloadVideo = parseMaybeJson(payload.video) || payload.video || {};
  const directThumbnail = payload.videoThumbnailUrl || payload.thumbnailUrl || payloadVideo?.thumbnailUrl || item.imageUrl;
  if (isValidUrl(directThumbnail)) return directThumbnail;

  const youtubeId = getYouTubeId(item.videoUrl || '');
  if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  return isValidUrl(item.imageUrl) ? item.imageUrl : null;
}

function isDirectVideoUrl(url: string | null | undefined) {
  if (!isValidUrl(url)) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || '');
}

function getVimeoId(url: string): string | null {
  if (!isValidUrl(url)) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (!host.includes('vimeo.com')) return null;

    const parts = parsed.pathname.split('/').filter(Boolean);
    const numericPart = parts.reverse().find((part) => /^\d+$/.test(part));
    return numericPart || null;
  } catch {
    return null;
  }
}

function getLoomId(url: string): string | null {
  if (!isValidUrl(url)) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (!host.includes('loom.com')) return null;

    const parts = parsed.pathname.split('/').filter(Boolean);
    const shareIndex = parts.findIndex((part) => part === 'share' || part === 'embed');
    if (shareIndex >= 0 && parts[shareIndex + 1]) return parts[shareIndex + 1];
  } catch {
    return null;
  }

  return null;
}

function getEmbedVideoUrl(url: string | null | undefined): string | null {
  if (!isValidUrl(url)) return null;
  if (isDirectVideoUrl(url)) return url || null;

  const youtubeId = getYouTubeId(url || '');
  if (youtubeId) {
    return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&fs=1&iv_load_policy=3`;
  }

  const vimeoId = getVimeoId(url || '');
  if (vimeoId) {
    return `https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0&badge=0`;
  }

  const loomId = getLoomId(url || '');
  if (loomId) {
    return `https://www.loom.com/embed/${loomId}?autoplay=1&hide_owner=true&hide_share=true&hide_title=true`;
  }

  try {
    const parsed = new URL(url || '');
    if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) return null;
    if (parsed.hostname.includes('vimeo.com')) return null;
    if (parsed.hostname.includes('loom.com')) return null;
  } catch {}

  return url || null;
}

function normalizeWhatsNewItem(raw: any): NormalizedWhatsNewItem {
  const createdAtIso = raw.createdAtIso ?? raw.createdat_iso ?? raw.createdat ?? null;
  const seenAtIso = raw.seenAtIso ?? raw.seenat_iso ?? raw.seenat ?? null;
  const readAtIso = raw.readAtIso ?? raw.readat_iso ?? raw.readat ?? null;
  const payloadJson = raw.payloadJson ?? raw.payloadjson;
  const notificationId = raw.notificationId ?? raw.notificationid ?? raw.id ?? 0;

  return {
    ...raw,
    id: raw.id ?? raw.notificationId ?? raw.notificationid ?? 0,
    notificationId,
    titleResolved: raw.titleResolved ?? raw.title_resolved ?? null,
    descriptionResolved: raw.descriptionResolved ?? raw.description_resolved ?? null,
    imageUrl: raw.imageUrl ?? raw.imageurl ?? null,
    videoUrl: raw.videoUrl ?? raw.videourl ?? null,
    payloadJson: parseMaybeJson(payloadJson),
    createdAtIso,
    seenAtIso,
    readAtIso,
    isRead: raw.isRead ?? Boolean(readAtIso),
    isSeen: raw.isSeen ?? Boolean(seenAtIso)
  };
}

function resolveI18nText(map: Record<string, string> | null | undefined, fallback: string | null | undefined, lang: string) {
  if (map?.[lang]) return map[lang];
  if (map?.es) return map.es;
  if (map?.en) return map.en;
  const firstValue = Object.values(map || {}).find((value) => typeof value === 'string' && value.trim());
  if (firstValue) return firstValue;
  return fallback || '';
}

function formatRelativeDate(iso: string | null | undefined, intl: IntlShape) {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);

  if (mins < 1) return intl.formatMessage({ id: 'whatsNew.time.justNow', defaultMessage: 'just now' });
  if (mins < 60) {
    return intl.formatMessage({ id: 'whatsNew.time.minutesAgo', defaultMessage: '{count} min ago' }, { count: mins });
  }

  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return intl.formatMessage({ id: 'whatsNew.time.hoursAgo', defaultMessage: '{count} hours ago' }, { count: hours });
  }

  return date.toLocaleDateString(intl.locale);
}

function getDateGroupLabel(iso: string | null | undefined, intl: IntlShape) {
  if (!iso) return '';
  const date = new Date(iso);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfItemDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfItemDay.getTime()) / 86400000);

  if (diffDays === 0) return intl.formatMessage({ id: 'whatsNew.today', defaultMessage: 'Today' });
  if (diffDays === 1) return intl.formatMessage({ id: 'whatsNew.yesterday', defaultMessage: 'Yesterday' });
  return date.toLocaleDateString(intl.locale);
}

function resolveTagLabel(tag: string | null | undefined, intl: IntlShape) {
  switch (String(tag || '').toUpperCase()) {
    case 'NEW_FEATURE':
      return intl.formatMessage({ id: 'whatsNew.tags.newFeature', defaultMessage: 'New Feature' });
    case 'IMPROVEMENT':
      return intl.formatMessage({ id: 'whatsNew.tags.improvement', defaultMessage: 'Improvement' });
    case 'ANNOUNCEMENT':
      return intl.formatMessage({ id: 'whatsNew.tags.announcement', defaultMessage: 'Announcement' });
    case 'MEETING':
      return intl.formatMessage({ id: 'whatsNew.tags.meeting', defaultMessage: 'Meeting' });
    case 'BUGFIX':
      return intl.formatMessage({ id: 'whatsNew.tags.bugfix', defaultMessage: 'Bug fix' });
    default:
      return intl.formatMessage({ id: 'whatsNew.tags.other', defaultMessage: 'Update' });
  }
}

function groupItemsByDate(items: NormalizedWhatsNewItem[], intl: IntlShape) {
  const groups = new Map<string, NormalizedWhatsNewItem[]>();

  items.forEach((item) => {
    const label = getDateGroupLabel(item.createdAtIso, intl) || intl.formatMessage({ id: 'whatsNew.today', defaultMessage: 'Today' });
    const current = groups.get(label) || [];
    current.push(item);
    groups.set(label, current);
  });

  return Array.from(groups.entries()).map(([label, groupedItems]) => ({ label, items: groupedItems }));
}

function isWhatsNewRealtimeEvent(evt: any) {
  const type = String(evt?.type || '').trim();
  const kind = String(evt?.kind || '').trim();
  const notificationType = String(evt?.notificationType || '').trim();
  const payloadType = String(evt?.payload?.type || '').trim();
  const payloadKind = String(evt?.payload?.kind || '').trim();

  return (
    type === 'WHATS_NEW' ||
    type === 'whats-new' ||
    kind === 'whats-new' ||
    notificationType === 'WHATS_NEW' ||
    payloadType === 'WHATS_NEW' ||
    payloadKind === 'whats-new'
  );
}

const WhatsNew = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { isInitialized, isLoggedIn } = useAuth();

  const app = useMemo(() => resolveAppFromHost(window.location.hostname), []);
  const lang = useMemo(() => (intl.locale?.startsWith('es') ? 'es' : intl.locale), [intl.locale]);

  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<NormalizedWhatsNewItem[]>([]);
  const [nextCursor, setNextCursor] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [expandedVideoId, setExpandedVideoId] = useState<number | null>(null);

  const seenIdsRef = useRef<Set<number>>(new Set());
  const openRef = useRef(false);
  const langRef = useRef(lang);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [wsRetryTick, setWsRetryTick] = useState(0);

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';

  const api = async (method: string, data?: any) => {
    const action: APIAction = { type: 'api', entity: 'notifications', method, data };
    return handleAction(action);
  };

  const handleToggle = () => setOpen((prev) => !prev);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    langRef.current = lang;
  }, [lang]);

  const refreshUnreadCount = async () => {
    if (!isInitialized || !isLoggedIn) return;

    try {
      const res: any = await api('whats-new-unread-count', { app });
      setUnreadCount(res?.unread ?? res?.count ?? 0);
    } catch (error) {
      console.error(error);
    }
  };

  const markItemsSeenBestEffort = (list: NormalizedWhatsNewItem[]) => {
    const pending = list.filter((item) => !item.isSeen && !seenIdsRef.current.has(item.id)).map((item) => item.id);
    if (pending.length === 0) return;

    pending.forEach((id) => seenIdsRef.current.add(id));

    Promise.allSettled(pending.map((id) => api('whats-new-seen', { id, app })))
      .then(() => {
        setItems((prev) =>
          prev.map((item) =>
            pending.includes(item.id) ? { ...item, isSeen: true, seenAtIso: item.seenAtIso || new Date().toISOString() } : item
          )
        );
        refreshUnreadCount();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const loadItems = async (mode: 'reset' | 'more' = 'reset') => {
    if (!isInitialized || !isLoggedIn) return;

    if (mode === 'reset') {
      setLoading(true);
      seenIdsRef.current = new Set();
    } else {
      setLoadingMore(true);
    }

    try {
      const res: any = await api('whats-new-list', {
        app,
        status: 'all',
        lang: langRef.current,
        limit: 20,
        cursor: mode === 'more' ? nextCursor : null
      });

      const normalizedItems = Array.isArray(res?.items) ? res.items.map(normalizeWhatsNewItem) : [];

      setItems((prev) => (mode === 'reset' ? normalizedItems : [...prev, ...normalizedItems]));
      setNextCursor(res?.nextCursor ?? null);
      markItemsSeenBestEffort(normalizedItems);
    } catch (error) {
      console.error(error);
    } finally {
      if (mode === 'reset') {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const markItemRead = async (item: NormalizedWhatsNewItem) => {
    if (item.isRead) return;

    try {
      await api('whats-new-read', { id: item.id, app });
      const nowIso = new Date().toISOString();
      setItems((prev) => prev.map((current) => (current.id === item.id ? { ...current, isRead: true, readAtIso: nowIso } : current)));
      refreshUnreadCount();
    } catch (error) {
      console.error(error);
    }
  };

  const openExternalUrl = async (url: string, item: NormalizedWhatsNewItem) => {
    if (!url) return;
    if (!item.isRead) {
      try {
        await markItemRead(item);
      } catch (error) {
        console.error(error);
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openInlineVideo = async (item: NormalizedWhatsNewItem) => {
    if (!isValidUrl(item.videoUrl)) return;
    if (!item.isRead) {
      try {
        await markItemRead(item);
      } catch (error) {
        console.error(error);
      }
    }
    setExpandedVideoId(item.id);
  };

  const markAllRead = async () => {
    setMarkingAllRead(true);

    try {
      await api('whats-new-read-all', { app });
      const nowIso = new Date().toISOString();
      setItems((prev) => prev.map((item) => ({ ...item, isRead: true, readAtIso: item.readAtIso || nowIso })));
      setUnreadCount(0);
    } catch (error) {
      console.error(error);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const scheduleRefreshUnreadCount = () => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(async () => {
      refreshTimerRef.current = null;
      await refreshUnreadCount();
    }, 250);
  };

  const scheduleReloadItems = () => {
    if (reloadTimerRef.current) return;
    reloadTimerRef.current = setTimeout(async () => {
      reloadTimerRef.current = null;
      await loadItems('reset');
    }, 500);
  };

  useEffect(() => {
    refreshUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isLoggedIn, app]);

  useEffect(() => {
    let cleanupUser: null | (() => void) = null;
    let cleanupApp: null | (() => void) = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const namespace = process.env.REACT_APP_APPSYNC_EVENTS_NAMESPACE || 'notifications';
    const idToken = localStorage.getItem('idToken') || '';

    if (!idToken) {
      // If auth is ready but token is not yet persisted, retry once shortly.
      if (isInitialized && isLoggedIn) {
        retryTimer = setTimeout(() => setWsRetryTick((v) => v + 1), 1200);
      }
      return () => {
        if (retryTimer) clearTimeout(retryTimer);
      };
    }

    const sub = getSubFromIdToken(idToken);
    if (!sub) {
      console.error('[AppSync Events][WhatsNew] Cannot decode sub from idToken');
      return;
    }

    const userChannelPath = `/${namespace}/user/${sub}`;
    const appChannelPath = `/${namespace}/app/${app}`;

    const handleRealtimeEvent = (source: 'user' | 'app') => async (evt: any) => {
      if (APPSYNC_EVENTS_WS_DEBUG) console.log(`[WhatsNew realtime] ${source} event`, evt);
      if (!isWhatsNewRealtimeEvent(evt)) {
        if (APPSYNC_EVENTS_WS_DEBUG) {
          console.log(`[WhatsNew realtime] ignored ${source} event`, {
            type: evt?.type,
            kind: evt?.kind,
            notificationType: evt?.notificationType,
            payloadType: evt?.payload?.type,
            payloadKind: evt?.payload?.kind
          });
        }
        return;
      }

      if (APPSYNC_EVENTS_WS_DEBUG) console.log('[WhatsNew realtime] refresh');
      scheduleRefreshUnreadCount();
      if (openRef.current) {
        scheduleReloadItems();
      }
    };

    try {
      if (APPSYNC_EVENTS_WS_DEBUG) {
        console.log('[WhatsNew realtime] subscribing user channel', userChannelPath);
        console.log('[WhatsNew realtime] subscribing app channel', appChannelPath);
      }

      cleanupUser = subscribeUserNotificationsWS({
        channelPath: userChannelPath,
        debug: APPSYNC_EVENTS_WS_DEBUG,
        onConnected: () => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.log('[WhatsNew realtime] user connected');
        },
        onEvent: handleRealtimeEvent('user'),
        onError: (err) => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.error('[WhatsNew realtime] user error', err);
        }
      });

      cleanupApp = subscribeUserNotificationsWS({
        channelPath: appChannelPath,
        debug: APPSYNC_EVENTS_WS_DEBUG,
        onConnected: () => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.log('[WhatsNew realtime] app connected');
        },
        onEvent: handleRealtimeEvent('app'),
        onError: (err) => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.error('[WhatsNew realtime] app error', err);
        }
      });
    } catch (err) {
      if (APPSYNC_EVENTS_WS_DEBUG) console.error('[WhatsNew realtime] subscribe init failed', err);
    }

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      cleanupUser?.();
      cleanupApp?.();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, isInitialized, isLoggedIn, wsRetryTick]);

  useEffect(() => {
    if (!open) return;
    loadItems('reset');
    refreshUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lang]);

  const groupedItems = useMemo(() => groupItemsByDate(items, intl), [items, intl]);

  const renderWhatsNewMedia = (item: NormalizedWhatsNewItem) => {
    const hasVideo = isValidUrl(item.videoUrl);
    const hasImage = isValidUrl(item.imageUrl);
    const thumbnail = getVideoThumbnail(item);
    const videoEmbedUrl = getEmbedVideoUrl(item.videoUrl);
    const isVideoExpanded = expandedVideoId === item.id;

    if (!hasVideo && !hasImage) return null;

    if (hasVideo) {
      if (isVideoExpanded) {
        return (
          <Box
            sx={{
              mt: 0.5,
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === ThemeMode.DARK ? 'grey.900' : 'common.black',
              aspectRatio: '16 / 9'
            }}
          >
            {isDirectVideoUrl(item.videoUrl) ? (
              <Box
                component="video"
                src={item.videoUrl || ''}
                controls
                autoPlay
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', bgcolor: 'common.black' }}
              />
            ) : videoEmbedUrl ? (
              <Box
                component="iframe"
                src={videoEmbedUrl}
                title={item.titleResolved || resolveI18nText(item.titleI18n, item.title, lang) || 'video'}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
              />
            ) : (
              <Stack sx={{ width: '100%', height: '100%' }} alignItems="center" justifyContent="center">
                <Typography variant="body2" color="common.white" sx={{ px: 2, textAlign: 'center' }}>
                  <FormattedMessage
                    id="whatsNew.video.unsupported"
                    defaultMessage="This video provider does not allow embedded playback with the current URL."
                  />
                </Typography>
              </Stack>
            )}
          </Box>
        );
      }

      return (
        <Box
          onClick={() => openInlineVideo(item)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openInlineVideo(item);
            }
          }}
          sx={{
            mt: 0.5,
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === ThemeMode.DARK ? 'grey.800' : 'grey.100',
            aspectRatio: '16 / 9',
            cursor: 'pointer'
          }}
        >
          {thumbnail ? (
            <Box component="img" src={thumbnail} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[500]} 100%)`
              }}
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              background:
                theme.palette.mode === ThemeMode.DARK
                  ? 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.4) 100%)'
                  : 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(0,0,0,0.22) 100%)',
              '&:hover .whats-new-play': {
                opacity: 1
              }
            }}
          >
            <Box
              className="whats-new-play"
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                opacity: 0.5,
                boxShadow: theme.shadows[6],
                transition: 'opacity 0.2s ease'
              }}
            >
              <PlayCircle variant="Bold" size={30} />
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={item.imageUrl || ''}
        alt=""
        sx={{
          width: '100%',
          maxHeight: 260,
          objectFit: 'cover',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          display: 'block'
        }}
      />
    );
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        onClick={handleToggle}
        aria-label="open what's new"
        size="large"
        sx={{ color: 'secondary.main', bgcolor: open ? iconBackColorOpen : iconBackColor, p: 1 }}
      >
        <Badge badgeContent={unreadCount} color="primary" sx={{ '& .MuiBadge-badge': { top: 2, right: 4 } }}>
          <NotificationStatus variant="Bulk" />
        </Badge>
      </IconButton>

      <Drawer
        sx={{ zIndex: 2001 }}
        anchor="right"
        onClose={handleToggle}
        open={open}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 600 },
            height: { xs: '100dvh', sm: '100%' },
            maxWidth: { xs: '100vw', sm: 600 },
            maxHeight: { xs: '100dvh', sm: '100%' },
            m: 0,
            borderRadius: { xs: 0, sm: 0 }
          }
        }}
      >
        {open && (
          <MainCard content={false} border={false} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <NotificationStatus variant="Bulk" />
                <Typography variant="h5">
                  <FormattedMessage id="whatsNew.title" defaultMessage="What's new announcement?" />
                </Typography>
              </Stack>
              <IconButton size="small" aria-label="close" color="error" onClick={handleToggle}>
                <Add style={{ transform: 'rotate(45deg)' }} />
              </IconButton>
            </Stack>

            <Divider />

            <Box sx={{ px: 3, py: 1.5 }}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="text"
                  disabled={markingAllRead || unreadCount === 0}
                  onClick={markAllRead}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {markingAllRead ? (
                    <FormattedMessage id="whatsNew.actions.loading" defaultMessage="Loading..." />
                  ) : (
                    <FormattedMessage id="whatsNew.actions.markAllRead" defaultMessage="Mark all as read" />
                  )}
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ flex: 1, minHeight: 0, px: 3, py: 3, overflowY: 'auto' }}>
              {loading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                  <CircularProgress size={28} />
                </Stack>
              ) : items.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="whatsNew.empty" defaultMessage="No updates yet." />
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={3}>
                  {groupedItems.map((group) => (
                    <Stack key={group.label} spacing={1.5}>
                      <Typography variant="h6" sx={{ px: 0.5 }}>
                        {group.label}
                      </Typography>

                      <Stack spacing={2}>
                        {group.items.map((item) => {
                          const payload = item.payloadJson || {};
                          const cta = parseMaybeJson(payload?.cta) || payload?.cta || null;
                          const title =
                            item.titleResolved ||
                            resolveI18nText(item.titleI18n, item.title, lang) ||
                            intl.formatMessage({ id: 'whatsNew.tags.other', defaultMessage: 'Update' });
                          const description =
                            item.descriptionResolved || resolveI18nText(item.descriptionI18n, item.description, lang) || '';
                          const ctaUrl = cta?.enabled !== false && isValidUrl(cta?.url) ? cta.url : '';
                          const ctaLabel =
                            resolveI18nText(cta?.labelI18n || null, null, lang) ||
                            intl.formatMessage({ id: 'whatsNew.actions.learnMore', defaultMessage: 'Learn more' });

                          return (
                            <Box
                              key={item.id}
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 3,
                                p: { xs: 2, sm: 2.5 },
                                bgcolor: 'background.paper',
                                boxShadow: theme.shadows[1]
                              }}
                            >
                              <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Chip
                                      label={resolveTagLabel(item.tag || item.type, intl)}
                                      size="small"
                                      sx={{
                                        borderRadius: 2,
                                        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.100',
                                        color: 'secondary.main',
                                        fontWeight: 600
                                      }}
                                    />
                                    {!item.isRead && (
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          bgcolor: 'primary.main',
                                          flexShrink: 0
                                        }}
                                      />
                                    )}
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatRelativeDate(item.createdAtIso, intl)}
                                  </Typography>
                                </Stack>

                                <Stack spacing={1}>
                                  <Typography variant="h4">{title}</Typography>
                                  {description ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                      {description}
                                    </Typography>
                                  ) : null}
                                </Stack>

                                {renderWhatsNewMedia(item)}

                                {ctaUrl ? (
                                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                    <Button
                                      variant="contained"
                                      onClick={() => openExternalUrl(ctaUrl, item)}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {ctaLabel}
                                    </Button>
                                  </Stack>
                                ) : null}
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Stack>
                  ))}

                  {nextCursor !== null ? (
                    <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                      <Button variant="text" disabled={loadingMore} onClick={() => loadItems('more')} sx={{ textTransform: 'none' }}>
                        {loadingMore ? (
                          <FormattedMessage id="whatsNew.actions.loading" defaultMessage="Loading..." />
                        ) : (
                          <FormattedMessage id="whatsNew.actions.loadMore" defaultMessage="Load more" />
                        )}
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              )}
            </Box>
          </MainCard>
        )}
      </Drawer>
    </Box>
  );
};

export default WhatsNew;
